import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Briefcase, Plus, Minus, Clock, MapPin } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';

interface Holiday {
  date: string;
  name: string;
  country?: string;
}

interface WorkingDaysResult {
  totalDays: number;
  workingDays: number;
  weekends: number;
  holidays: number;
  breakdown: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
}

export const WorkingDaysCalculatorTool: React.FC = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [workingDaysPerWeek, setWorkingDaysPerWeek] = useState(5);
  const [customHolidays, setCustomHolidays] = useState<Holiday[]>([]);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');
  const [result, setResult] = useState<WorkingDaysResult | null>(null);
  const [country, setCountry] = useState('US');

  // Common holidays for different countries
  const commonHolidays: { [key: string]: Holiday[] } = {
    US: [
      { date: '2024-01-01', name: "New Year's Day" },
      { date: '2024-01-15', name: 'Martin Luther King Jr. Day' },
      { date: '2024-02-19', name: "Presidents' Day" },
      { date: '2024-05-27', name: 'Memorial Day' },
      { date: '2024-07-04', name: 'Independence Day' },
      { date: '2024-09-02', name: 'Labor Day' },
      { date: '2024-10-14', name: 'Columbus Day' },
      { date: '2024-11-11', name: 'Veterans Day' },
      { date: '2024-11-28', name: 'Thanksgiving Day' },
      { date: '2024-12-25', name: 'Christmas Day' }
    ],
    UK: [
      { date: '2024-01-01', name: "New Year's Day" },
      { date: '2024-03-29', name: 'Good Friday' },
      { date: '2024-04-01', name: 'Easter Monday' },
      { date: '2024-05-06', name: 'Early May Bank Holiday' },
      { date: '2024-05-27', name: 'Spring Bank Holiday' },
      { date: '2024-08-26', name: 'Summer Bank Holiday' },
      { date: '2024-12-25', name: 'Christmas Day' },
      { date: '2024-12-26', name: 'Boxing Day' }
    ],
    CN: [
      { date: '2024-01-01', name: "New Year's Day" },
      { date: '2024-02-10', name: 'Spring Festival' },
      { date: '2024-02-11', name: 'Spring Festival' },
      { date: '2024-02-12', name: 'Spring Festival' },
      { date: '2024-04-04', name: 'Tomb Sweeping Day' },
      { date: '2024-05-01', name: 'Labor Day' },
      { date: '2024-06-10', name: 'Dragon Boat Festival' },
      { date: '2024-09-17', name: 'Mid-Autumn Festival' },
      { date: '2024-10-01', name: 'National Day' },
      { date: '2024-10-02', name: 'National Day' },
      { date: '2024-10-03', name: 'National Day' }
    ]
  };

  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'UK', name: 'United Kingdom' },
    { code: 'CN', name: 'China' },
    { code: 'CUSTOM', name: 'Custom Holidays Only' }
  ];

  const isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  const isHoliday = (date: Date, holidays: Holiday[]): boolean => {
    const dateString = date.toISOString().split('T')[0];
    return holidays.some(holiday => holiday.date === dateString);
  };

  const calculateWorkingDays = (): WorkingDaysResult => {
    if (!startDate || !endDate) {
      return {
        totalDays: 0,
        workingDays: 0,
        weekends: 0,
        holidays: 0,
        breakdown: {
          monday: 0,
          tuesday: 0,
          wednesday: 0,
          thursday: 0,
          friday: 0,
          saturday: 0,
          sunday: 0
        }
      };
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start > end) {
      return {
        totalDays: 0,
        workingDays: 0,
        weekends: 0,
        holidays: 0,
        breakdown: {
          monday: 0,
          tuesday: 0,
          wednesday: 0,
          thursday: 0,
          friday: 0,
          saturday: 0,
          sunday: 0
        }
      };
    }

    const holidays = country === 'CUSTOM' ? customHolidays : [...(commonHolidays[country] || []), ...customHolidays];
    
    let totalDays = 0;
    let workingDays = 0;
    let weekends = 0;
    let holidayCount = 0;
    
    const breakdown = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0
    };

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    const current = new Date(start);
    while (current <= end) {
      totalDays++;
      const dayOfWeek = current.getDay();
      const dayName = dayNames[dayOfWeek] as keyof typeof breakdown;
      breakdown[dayName]++;
      
      const isWeekendDay = isWeekend(current);
      const isHolidayDay = isHoliday(current, holidays);
      
      if (isWeekendDay) {
        weekends++;
      }
      
      if (isHolidayDay) {
        holidayCount++;
      }
      
      // Count as working day if:
      // 1. Not a holiday
      // 2. Either not a weekend, or weekends are included
      if (!isHolidayDay && (!isWeekendDay || includeWeekends)) {
        workingDays++;
      }
      
      current.setDate(current.getDate() + 1);
    }

    return {
      totalDays,
      workingDays,
      weekends,
      holidays: holidayCount,
      breakdown
    };
  };

  const addCustomHoliday = () => {
    if (!newHolidayDate || !newHolidayName.trim()) {
      return;
    }

    const newHoliday: Holiday = {
      date: newHolidayDate,
      name: newHolidayName.trim()
    };

    setCustomHolidays([...customHolidays, newHoliday]);
    setNewHolidayDate('');
    setNewHolidayName('');
  };

  const removeCustomHoliday = (index: number) => {
    setCustomHolidays(customHolidays.filter((_, i) => i !== index));
  };

  const setToday = (setter: (value: string) => void) => {
    const today = new Date().toISOString().split('T')[0];
    setter(today);
  };

  const addBusinessDays = (days: number) => {
    if (!startDate) return;
    
    const start = new Date(startDate);
    let current = new Date(start);
    let addedDays = 0;
    
    while (addedDays < days) {
      current.setDate(current.getDate() + 1);
      
      if (!isWeekend(current) || includeWeekends) {
        const holidays = country === 'CUSTOM' ? customHolidays : [...(commonHolidays[country] || []), ...customHolidays];
        if (!isHoliday(current, holidays)) {
          addedDays++;
        }
      }
    }
    
    setEndDate(current.toISOString().split('T')[0]);
  };

  // Auto-calculate when inputs change
  useEffect(() => {
    const calculatedResult = calculateWorkingDays();
    setResult(calculatedResult);
  }, [startDate, endDate, includeWeekends, customHolidays, country]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Briefcase className="w-5 h-5" />
            <span>Working Days Calculator</span>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Date Range Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="w-5 h-5" />
            <span>Date Range</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date
              </label>
              <div className="flex space-x-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <Button
                  onClick={() => setToday(setStartDate)}
                  variant="outline"
                  size="sm"
                >
                  Today
                </Button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <div className="flex space-x-2">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <Button
                  onClick={() => setToday(setEndDate)}
                  variant="outline"
                  size="sm"
                >
                  Today
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => addBusinessDays(30)}
              variant="outline"
              size="sm"
            >
              +30 Business Days
            </Button>
            <Button
              onClick={() => addBusinessDays(60)}
              variant="outline"
              size="sm"
            >
              +60 Business Days
            </Button>
            <Button
              onClick={() => addBusinessDays(90)}
              variant="outline"
              size="sm"
            >
              +90 Business Days
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MapPin className="w-5 h-5" />
            <span>Settings</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Country/Region
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={includeWeekends}
                  onChange={(e) => setIncludeWeekends(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm">Include weekends as working days</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Custom Holidays */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>Custom Holidays</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                type="date"
                value={newHolidayDate}
                onChange={(e) => setNewHolidayDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <input
                type="text"
                value={newHolidayName}
                onChange={(e) => setNewHolidayName(e.target.value)}
                placeholder="Holiday name"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <Button
                onClick={addCustomHoliday}
                disabled={!newHolidayDate || !newHolidayName.trim()}
                variant="primary"
                size="sm"
              >
                Add Holiday
              </Button>
            </div>

            {customHolidays.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Custom Holidays:
                </h4>
                {customHolidays.map((holiday, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                  >
                    <span className="text-sm">
                      {holiday.date} - {holiday.name}
                    </span>
                    <Button
                      onClick={() => removeCustomHoliday(index)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && result.totalDays > 0 && (
        <>
          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="w-5 h-5" />
                <span>Calculation Results</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {result.workingDays}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Working Days</div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                    {result.totalDays}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Days</div>
                </div>
                
                <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {result.weekends}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Weekend Days</div>
                </div>
                
                <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {result.holidays}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Holidays</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Day Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Day Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2 text-center">
                {Object.entries(result.breakdown).map(([day, count]) => (
                  <div key={day} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {count}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                      {day.slice(0, 3)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Features:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Calculate working days between dates</li>
                <li>• Multiple country holiday calendars</li>
                <li>• Custom holiday management</li>
                <li>• Weekend inclusion option</li>
                <li>• Detailed day breakdown</li>
                <li>• Quick business day addition</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Use Cases:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Project timeline planning</li>
                <li>• Contract duration calculation</li>
                <li>• Payroll and HR management</li>
                <li>• Delivery date estimation</li>
                <li>• Business planning and scheduling</li>
                <li>• Legal deadline calculations</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
